import { globalStores } from '@/editor/stores';
import { AreaDimensionEntity } from '@/editor/entities/areaDimensionEntity';
import { EEntityType, ESceneMode, ISelected } from '@/editor/types';
import { AreaDimensionEndPointEntity } from '@/editor/entities/areaDimensionEndpointEntity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { LinearDimensionEntity } from '@/editor/entities/linearDimensionEntity';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';

export class DimensionManager {
  private _areaMap: Map<string, AreaDimensionEntity>;
  private _areaEndPointMap: Map<string, AreaDimensionEndPointEntity> = new Map();
  private _linearMap: Map<string, LinearDimensionEntity> = new Map();
  private _disposeArr: (() => void)[] = [];

  constructor() {
    this._areaMap = new Map();
    this._disposeArr.push(editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._updateScale));
    this._disposeArr.push(editorEvent.on(EditorEventName.VIEW2D_CHANGE, this._handleViewChange));
    this._disposeArr.push(editorEvent.on(EditorEventName.RESIZE, this._handleViewChange));
    this._disposeArr.push(editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this._sceneModeChangeHandler));
  }

  public getEntity(id: string) {
    return this._areaMap.get(id) ?? this._linearMap.get(id);
  }

  // ========== 面积标注 ========= //
  public getAreaDimension(id: string) {
    return this._areaMap.get(id);
  }

  private _addAreaDimensionEndpoint(point: IPoint2d, theme: 'normal' | 'highlight' = 'normal') {
    const id = `${point.x}-${point.y}`;
    this.removeEndPoint(point);

    const scale = this.getScale();
    const newEntity = new AreaDimensionEndPointEntity();
    newEntity.updatePoint(point, scale, theme);
    this._areaEndPointMap.set(id, newEntity);
  }

  public addHighlightEndPoint(point: IPoint2d) {
    this._addAreaDimensionEndpoint(point, 'highlight');
  }

  public addEndPoint(point: IPoint2d) {
    this._addAreaDimensionEndpoint(point);
  }

  public removeEndPoint(point: IPoint2d) {
    const id = `${point.x}-${point.y}`;
    const oldEntity = this._areaEndPointMap.get(id);
    oldEntity?.dispose();
    this._areaEndPointMap.delete(id);
  }

  public removeAllEndPoints() {
    for (const entity of this._areaEndPointMap.values()) {
      entity.dispose();
    }
    this._areaEndPointMap.clear();
  }

  private _updateAreaDimension() {
    const areaDimensions = globalStores.dimensionStore.getAreaDimList();
    const hiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.AreaDimension);
    const nextAreaIds = new Set(
      areaDimensions.map((areaDimension) => areaDimension.id).filter((id) => !hiddenIds.has(id))
    );

    for (const [id, entity] of this._areaMap) {
      if (nextAreaIds.has(id)) continue;

      entity.dispose();
      this._areaMap.delete(id);
    }

    for (const areaDimension of areaDimensions) {
      if (hiddenIds.has(areaDimension.id)) continue;
      const entity = this._areaMap.get(areaDimension.id);
      if (entity) {
        entity.update(areaDimension);
      } else {
        this._areaMap.set(areaDimension.id, new AreaDimensionEntity(areaDimension));
      }
    }
  }

  // ========= 距离标注 ========== //
  public getLinearDimension(id: string) {
    return this._linearMap.get(id);
  }

  private _updateLinearDimension() {
    const linearDimensions = globalStores.dimensionStore.getLinearDimList();
    const hiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.LinearDimension);
    // 布局模式下一律不渲染手绘尺寸标注。走这个正交判断而不是往隐藏集里写 id：
    // 隐藏集是用户自己的显隐状态，被临时覆写过就再也还原不回「部分隐藏」了
    const hideAll = globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;
    const isHidden = (id: string) => hideAll || hiddenIds.has(id);
    const nextLinearIds = new Set(
      linearDimensions.map((linearDimension) => linearDimension.id).filter((id) => !isHidden(id))
    );
    for (const [id, entity] of this._linearMap) {
      if (nextLinearIds.has(id)) continue;

      entity.dispose();
      this._linearMap.delete(id);
    }

    for (const linearDimension of linearDimensions) {
      if (isHidden(linearDimension.id)) continue;
      const entity = this._linearMap.get(linearDimension.id);
      if (entity) {
        entity.update(linearDimension);
      } else {
        this._linearMap.set(linearDimension.id, new LinearDimensionEntity(linearDimension));
      }
    }
  }

  public update() {
    this._updateAreaDimension();
    this._updateLinearDimension();
  }

  public updateHover(_selected: ISelected) {}

  public updateSelected(entityId: string, selected: boolean) {
    const entity = this.getEntity(entityId);
    if (entity) {
      entity.selected = selected;
    }
  }

  private getScale(zoomLv: number = globalStores.editorStateStore.sceneInfo.scene2d.camera.zoom * 100) {
    return 100 / zoomLv;
  }

  private _handleViewChange = () => {
    for (const entity of this._linearMap.values()) {
      entity.updateTextView();
    }
  };

  private _sceneModeChangeHandler = (sceneMode: ESceneMode) => {
    requestAnimationFrame(() => {
      for (const entity of this._linearMap.values()) {
        entity.updateTextVisible(sceneMode === ESceneMode.scene2d);
      }
    });
  };

  private _updateScale = (zoomLv: number) => {
    const scale = this.getScale(zoomLv);
    for (const entity of this._areaEndPointMap.values()) {
      entity.updateScale(scale);
    }

    for (const entity of this._linearMap.values()) {
      entity.updateScale(scale);
    }
  };

  public dispose() {
    for (const disposeFunc of this._disposeArr) {
      disposeFunc?.();
    }
    for (const entity of this._areaMap.values()) {
      entity.dispose();
    }
    this._areaMap.clear();

    for (const entity of this._linearMap.values()) {
      entity.dispose();
    }
    this._linearMap.clear();
    this._disposeArr.length = 0;
  }
}
